import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_BASE_URL } from 'src/app/web-api-client';
import { CountryAdminPermissions } from '@shared/models/country-admin-permissions';

interface CountryAdminPermissionsResponse {
  isCountryAdmin?: boolean;
  canEditAnyCountry?: boolean;
  countryAdminEditScope?: unknown;
  assignedCountryIds?: unknown;
  countryIds?: unknown;
  assignedCountries?: unknown;
  countries?: unknown;
  value?: CountryAdminPermissionsResponse;
}

const MAX_COUNTRY_ID_DEPTH = 3;

@Injectable({
  providedIn: 'root',
})
export class CountryAdminPermissionsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  get(): Observable<CountryAdminPermissions> {
    return this.http
      .get<CountryAdminPermissionsResponse>(
        `${this.baseUrl}/api/v1/CountryAdminPermissions`
      )
      .pipe(map(response => this.mapFromResponse(response)));
  }

  private mapFromResponse(
    response: CountryAdminPermissionsResponse
  ): CountryAdminPermissions {
    const payload = response?.value ?? response ?? {};

    return {
      isCountryAdmin: !!payload.isCountryAdmin,
      countryAdminEditScope: this.toCountryIds(
        payload.countryAdminEditScope ??
          payload.assignedCountryIds ??
          payload.countryIds ??
          payload.assignedCountries ??
          payload.countries
      ),
      canEditAnyCountry: !!payload.canEditAnyCountry,
    };
  }

  /**
   * Scope may arrive as ids, numeric strings, or country objects depending on the endpoint version.
   */
  private toCountryIds(source: unknown): number[] {
    if (!Array.isArray(source)) {
      return [];
    }

    return source
      .map(item => this.toCountryId(item))
      .filter((id): id is number => id !== undefined);
  }

  private toCountryId(item: unknown, depth = 0): number | undefined {
    if (typeof item === 'number') {
      return Number.isFinite(item) ? item : undefined;
    }

    if (typeof item === 'string' && item.trim() !== '') {
      const parsed = Number(item);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    if (item && typeof item === 'object' && depth < MAX_COUNTRY_ID_DEPTH) {
      const record = item as Record<string, unknown>;
      return this.toCountryId(
        record['countryId'] ?? record['id'] ?? record['country'],
        depth + 1
      );
    }

    return undefined;
  }
}
